/**
 * Feedback - embeds the Canny feedback board + roadmap in-app. Users reach it from the left nav
 * ("Feedback", below Help) and post/vote without a separate Canny login (we identify them with their
 * Upstand identity). Config: VITE_CANNY_APP_ID + VITE_CANNY_BOARD_TOKEN (see lib/canny.ts). The nav
 * item is hidden until both are set, so this fallback is only reachable via the /feedback URL.
 */
import { useEffect } from 'react';
import { useAuth } from '@/contexts/auth-context';
import {
  cannyConfigured,
  fetchCannySsoToken,
  loadCannySdk,
  renderCannyBoard,
} from '@/lib/canny';

export function FeedbackPage() {
  const { user } = useAuth();
  const configured = cannyConfigured();

  useEffect(() => {
    if (!configured) return;
    let cancelled = false;
    loadCannySdk();
    // Sign the user in with a server-minted SSO token so Canny doesn't show its own signup modal.
    // Identity is derived server-side from the JWT (canny-sso edge fn); on failure we render the
    // board anonymously (Canny falls back to its signup).
    void (async () => {
      const ssoToken = user ? await fetchCannySsoToken() : null;
      if (cancelled) return;
      renderCannyBoard(ssoToken);
    })();
    return () => {
      cancelled = true;
    };
  }, [configured, user]);

  return (
    <div className="mx-auto max-w-6xl px-6 py-8 lg:px-12 lg:py-12">
      <header className="mb-8">
        <p className="mb-2 font-display text-[10px] font-bold uppercase tracking-[0.25em] text-tertiary">
          Feedback
        </p>
        <h1 className="mb-3 font-display text-3xl font-light leading-tight tracking-tight text-headline md:text-4xl">
          Share your ideas
        </h1>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Request features, report issues, and vote on what we build next.
        </p>
      </header>

      {configured ? (
        <div data-canny className="min-h-[400px]" />
      ) : import.meta.env.DEV ? (
        <div className="rounded-lg border border-dashed border-border bg-surface-container-low/40 p-8 text-center">
          <p className="text-sm font-medium text-foreground">Feedback isn’t configured yet</p>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
            Set <code className="rounded bg-muted px-1 py-0.5 text-xs">VITE_CANNY_APP_ID</code> and{' '}
            <code className="rounded bg-muted px-1 py-0.5 text-xs">VITE_CANNY_BOARD_TOKEN</code> in the
            frontend build environment to enable the Canny board.
          </p>
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-border bg-surface-container-low/40 p-8 text-center">
          <p className="text-sm text-muted-foreground">Feedback isn’t available right now.</p>
        </div>
      )}
    </div>
  );
}
